import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TournamentService } from '../tournament/tournament.service';
import { LoggingService } from '../shared/logging/logging.service';
import { ReportContextOption } from '../../models/report-context-option';

@Injectable({ providedIn: 'root' })
export class ReportContextOptionsService {
    private readonly tournamentService = inject(TournamentService);
    private readonly loggingService = inject(LoggingService);

    private readonly roundCache = new Map<string, ReportContextOption[]>();
    private readonly courseCache = new Map<string, ReportContextOption[]>();
    private readonly roundInFlight = new Map<string, Promise<ReportContextOption[]>>();
    private readonly courseInFlight = new Map<string, Promise<ReportContextOption[]>>();

    async getRoundOptions(tournamentId: string | number | null | undefined): Promise<ReportContextOption[]> {
        const cacheKey = this.normalizeTournamentId(tournamentId);
        if (!cacheKey) {
            return [];
        }

        if (this.roundCache.has(cacheKey)) {
            return this.roundCache.get(cacheKey)!;
        }

        let pending = this.roundInFlight.get(cacheKey);
        if (!pending) {
            pending = firstValueFrom(this.tournamentService.getRounds(cacheKey))
                .then(rounds => {
                    const options = rounds
                        .filter(round => !!round.roundId)
                        .map(round => {
                            const id = this.normalizeId(round.roundId);
                            const option: ReportContextOption = {
                                id,
                                label: round.number ? `Round ${round.number}` : id,
                            };
                            return option;
                        })
                        .filter(option => !!option.id && option.id.length > 0);

                    this.roundCache.set(cacheKey, options);
                    this.roundInFlight.delete(cacheKey);
                    return options;
                })
                .catch(error => {
                    this.loggingService.error('ReportContextOptionsService', `Failed to load rounds for tournament ${cacheKey}`, error);
                    this.roundInFlight.delete(cacheKey);
                    return [];
                });

            this.roundInFlight.set(cacheKey, pending);
        }

        return pending;
    }

    async getCourseOptions(tournamentId: string | number | null | undefined): Promise<ReportContextOption[]> {
        const cacheKey = this.normalizeTournamentId(tournamentId);
        if (!cacheKey) {
            return [];
        }

        if (this.courseCache.has(cacheKey)) {
            return this.courseCache.get(cacheKey)!;
        }

        let pending = this.courseInFlight.get(cacheKey);
        if (!pending) {
            pending = firstValueFrom(this.tournamentService.getTournamentCoursesByTournamentId(cacheKey))
                .then(courses => {
                    const options = courses
                        .filter(course => !!course.courseId)
                        .map(course => {
                            const id = this.normalizeId(course.courseId);
                            const option: ReportContextOption = {
                                id,
                                label: course.courseName?.trim().length ? course.courseName : `Course ${course.courseId}`,
                            };
                            return option;
                        })
                        .filter(option => !!option.id && option.id.length > 0);

                    this.courseCache.set(cacheKey, options);
                    this.courseInFlight.delete(cacheKey);
                    return options;
                })
                .catch(error => {
                    this.loggingService.error('ReportContextOptionsService', `Failed to load courses for tournament ${cacheKey}`, error);
                    this.courseInFlight.delete(cacheKey);
                    return [];
                });

            this.courseInFlight.set(cacheKey, pending);
        }

        return pending;
    }

    clearCachedOptions(tournamentId?: string | number | null): void {
        if (tournamentId === undefined) {
            this.roundCache.clear();
            this.courseCache.clear();
            this.roundInFlight.clear();
            this.courseInFlight.clear();
            return;
        }

        const cacheKey = this.normalizeTournamentId(tournamentId);
        if (!cacheKey) {
            return;
        }
        this.roundCache.delete(cacheKey);
        this.courseCache.delete(cacheKey);
        this.roundInFlight.delete(cacheKey);
        this.courseInFlight.delete(cacheKey);
    }

    private normalizeTournamentId(value: string | number | null | undefined): string | null {
        if (value === null || value === undefined) {
            return null;
        }

        const trimmed = typeof value === 'string' ? value.trim() : value;
        if (typeof trimmed === 'string' && trimmed.length === 0) {
            return null;
        }

        return String(trimmed);
    }

    private normalizeId(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value);
    }
}
